import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import propTypes from "@styled-system/prop-types";
import { useController, useForm } from "react-hook-form";
import { nanoid } from "nanoid";
import { pick, omit } from "lodash";
import {
  FormInputWrapper,
  FormLabelStyle,
  FormInputGroupStyle,
  FormInputGroupAppendStyle,
  FormTextAreaStyle,
  FormHelpStyle,
  FormInvalidFeedbackStyle,
} from "./forms.style";
import Button from "../button";

const FormTextArea = ({
  innerRef,
  validationRules,
  control: controlProp,
  formState: formStateProp,
  inline,
  label,
  name,
  defaultValue,
  id,
  helpText,
  spiMask,
  timeout,
  onInput,
  onChange,
  onBlur,
  appendButton,
  buttonText,
  buttonColor,
  buttonLoading,
  buttonDisabled,
  onButtonClick,
  ...props
}) => {
  const formHook = useForm();
  const control = controlProp || formHook.control;
  const formState = formStateProp || formHook.formState;
  const [isRequired] = useState(!!validationRules?.required);
  const [error, setError] = useState(formState?.errors[name] ?? undefined);
  const inputId = id || `textarea_${nanoid()}`;
  const spacePropsKeys = Object.keys(propTypes.space);
  const spaceProps = pick(props, spacePropsKeys);
  const attributes = omit(props, spacePropsKeys);
  const {
    field: { onChange: oc, onBlur: ob, value, ref: originalInput },
    meta: { invalid },
  } = useController({
    name,
    control,
    rules: validationRules,
    defaultValue: defaultValue || "",
    onFocus() {
      if (originalInput.current instanceof Element) {
        originalInput.current.focus();
      }
    }
  });

  /* Set field error */
  useEffect(() => {
    if (!formState?.errors) {
      setError(undefined);
    } else if (formState.errors[name] !== error) {
      setError(formState.errors[name]);
    }
  }, [formState, name, error]);

  const onInputChange = (e) => {
    oc(e);
    onChange && onChange(e);
  };

  const onInputBlur = (e) => {
    ob();
    onBlur && onBlur(e);
  };

  const renderInput = () => (
    <>
      <FormTextAreaStyle
        ref={originalInput}
        name={name}
        id={inputId}
        value={value}
        invalid={invalid}
        onInput={onInput}
        onChange={onInputChange}
        onBlur={onInputBlur}
        mt={0}
        {...attributes}
      />
    </>
  );

  const renderInputHelpers = () => (
    <>
      {helpText && <FormHelpStyle>{helpText}</FormHelpStyle>}
      {error && <FormInvalidFeedbackStyle role="alert">{error.message}</FormInvalidFeedbackStyle>}
    </>
  );

  return (
    <FormInputWrapper {...spaceProps} ref={innerRef} inline={inline}>
      {label && <FormLabelStyle htmlFor={inputId}>{label}{isRequired && ` *`}</FormLabelStyle>}
      {!appendButton && renderInput()}
      {appendButton && (
        <FormInputGroupStyle>
          {renderInput()}
          <FormInputGroupAppendStyle>
            <Button type="button" colors={buttonColor} loading={buttonLoading}
                    disabled={buttonDisabled}
                    onClick={onButtonClick}>{buttonText}</Button>
          </FormInputGroupAppendStyle>
          {renderInputHelpers()}
        </FormInputGroupStyle>
      )}
      {!appendButton && renderInputHelpers()}
    </FormInputWrapper>
  );
};

FormTextArea.propTypes = {
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  validationRules: PropTypes.object,
  watch: PropTypes.func,
  control: PropTypes.object,
  formState: PropTypes.object,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  helpText: PropTypes.string,
  timeout: PropTypes.number,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  appendButton: PropTypes.bool,
  buttonText: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonLoading: PropTypes.bool,
  buttonDisabled: PropTypes.bool,
  onButtonClick: PropTypes.func,
  ...propTypes.space,
};

FormTextArea.defaultProps = {
  inline: false,
  disabled: false,
  defaultValue: "",
};

export default FormTextArea;
